import React from "react";
import useDemandResponseContext from "../../context/useDemandResponseContext";
import useAppContextProvider from "../../../AppContext/useAppContextProvider";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog";
import {
  H6HeadlineSmall,
  H8TitleMedium,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  StyledIconButton,
  StyledRadio,
  StyledToggleButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import PlaceHolderButton from "../../../components/PlaceHolderButton/PlaceHolderButton.js";
import CheckIcon from "@mui/icons-material/Check";
import {
  StyledFormControl,
  StyledTextField,
  StyledPaper,
  StyledSelect,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { useCreateQSE } from "../../queries/useCreateQSE.ts";
import { useAttachQSE } from "../../queries/useAttachQSE.ts";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import { StyledMenuItem } from "../../../components/StyledComponents/Menu/Menu.tsx";

const AddDemandConfigModalHeader = (props) => {
  const { openQSEDialog, setOpenQSEDialog } =
    useDemandResponseContext();
  const { reset } = props;
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1rem 0"}
    >
      <H6HeadlineSmall>
        Assign a Qualified Scheduling Entity
      </H6HeadlineSmall>
      <StyledIconButton colors={colors}
        onClick={() => {
          setOpenQSEDialog(false);
          reset();
        }}
      >
        <CloseIcon />
      </StyledIconButton>
    </Box>
  );
};

const AssignQSEDialog = () => {
  const {
    openQSEDialog,
    setOpenQSEDialog,
    siteLists,
    setSiteSelectedName,
  } = useDemandResponseContext();
  const { colors } = useTheme();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const {
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid },
    reset,
    clearErrors,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      siteConfig: "",
      qse: null,
      username: "",
      password: "",
      optIn: "true",
      rampUpDownTime: null,
    },
  });
  const { mutate: addPriceDemandConfig } = useCreateQSE();
  const { mutate: attachQSE } = useAttachQSE();
  const handleAttachQSE = async (data) => {
    const site = data.siteConfig;
    addPriceDemandConfig(
      {
        config_name: site,
        config_type: "demandResponseConfig",
        config: {
          QSE: data.qse,
          ...(data.qse === "Voltus" && {
            VoltusOptIn:
              data.optIn === "true" ? true : false,
          }),
          rampupdown_time: isNaN(
            parseInt(data.rampUpDownTime)
          )
            ? 0
            : parseInt(data.rampUpDownTime),
          ...(data.qse === "CPower" && {
            Username: data.username,
            Password: data.password,
          }),
        },
      },
      {
        onSuccess: async (res) => {
          if (res.status === "ok") {
            setToastMessage(
              "QSE created successfully, performing attach operation"
            );
            setToastOpen(true);
            attachQSE(
              {
                attach: true,
                config_type: "demandResponseConfig",
                config_name: data.siteConfig,
                sites: [data.siteConfig],
              },
              {
                onSuccess: async (res) => {
                  if (res.status === "ok") {
                    setToastMessage(
                      "QSE was created and attached successfully. The job will be created once the dispatch event is received from QSE."
                    );
                    setToastOpen(true);
                    setOpenQSEDialog(false);
                    reset();
                  } else {
                    setToastMessage(
                      "Attaching QSE operation failed"
                    );
                    setToastOpen(true);
                  }
                },
                onError: (error) => {
                  console.error(
                    "Error attaching QSE:",
                    error
                  );
                  setToastMessage("Failed to attach QSE");
                  setToastOpen(true);
                },
              }
            );
          } else {
            setToastMessage(
              "Creating QSE operation failed"
            );
            setToastOpen(true);
          }
        },
        onError: (error) => {
          console.error("Error creating QSE:", error);
          setToastMessage("Failed to create QSE");
          setToastOpen(true);
        },
      }
    );
    // Once all addPriceDemandConfig calls are complete, call attachQSE
  };
  const onError = (errors) => {
    console.log("Form errors", errors);
    setToastMessage("Please fill all the required fields");
    setToastOpen(true);
  };
  const onsubmit = async (data) => {
    console.log("data inside qse", data);
    await handleAttachQSE(data);
    setOpenQSEDialog(false);
    reset();
  };

  const selectedOption = watch("siteConfig");
  const selectedQSE = watch("qse");
  const enteredRampUpDownTime = watch("rampUpDownTime");
  const handleSiteConfigDeletion = (optionToDelete) => {
    const newValue = selectedOption.filter(
      (option) => option !== optionToDelete
    );
    setValue("siteConfig", newValue);
  };

  React.useEffect(() => {
    if (selectedQSE === "Voltus") {
      clearErrors(["username", "password"]);
    } else if (selectedQSE === "CPower") {
      clearErrors(["optIn"]);
    }
  }, [selectedQSE, clearErrors]);

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={openQSEDialog}
      hideBackdrop
    >
      <AddDemandConfigModalHeader reset={reset} />
      <form onSubmit={handleSubmit(onsubmit, onError)}>
        <DialogContent
          sx={{
            maxHeight: "60vh",
            overflowY: "auto",
            padding: 0,
          }}
        >
          <Box
            display={"grid"}
            gridTemplateRows={"auto auto auto"}
            rowGap={7.75}
            sx={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            <Box
              display={"grid"}
              rowGap={4}
              gridTemplateRows={"auto auto auto"}
              className="sites-container"
            >
              <H8TitleMedium>
                Select Sites to Assign Demand Response
                Configuration
              </H8TitleMedium>
              <StyledFormControl colors={colors} fullWidth margin="none">
                <InputLabel>Site</InputLabel>
                {/* <InputLabel htmlFor="">Status</InputLabel> */}
                <Controller
                  name="siteConfig"
                  control={control}
                  rules={{
                    required: "Site is required",
                    validate: (value) => {
                      if (!value || value.length === 0) {
                        return "Please select at least one site";
                      }
                    },
                  }}
                  defaultValue={null}
                  render={({ field }) => (
                    <StyledSelect
                      {...field}
                      label="Site"
                      colors={colors}
                      value={field.value || ""}
                      style={{ flex: 1 }}
                      onChange={(event) => {
                        const value = event.target.value;
                        field.onChange(value);
                        setSiteSelectedName(value);
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: "250px",
                            overflowY: "auto",
                            backgroundColor: colors.background.paper,
                            color: colors.text.primary,
                          },
                        },
                      }}
                    >
                      {siteLists?.map((siteName, index) => (
                        <StyledMenuItem colors={colors}
                          key={index}
                          value={siteName}
                        >
                          {siteName}
                        </StyledMenuItem>
                      ))}
                    </StyledSelect>
                  )}
                />
              </StyledFormControl>
            </Box>
            <Box
              display={"grid"}
              gridTemplateRows={"auto auto"}
              rowGap={4}
            >
              <H8TitleMedium>
                Select Qualified Scheduling Entity
              </H8TitleMedium>
              <Box
                display={"grid"}
                gridTemplateColumns={
                  "repeat(auto-fill, minmax(6rem,1fr) )"
                }
                gap={4}
              >
                <Controller
                  name="qse"
                  control={control}
                  defaultValue={null}
                  rules={{
                    required: "QSE is required",
                    validate: (value) => {
                      if (!value) {
                        return "Please select one QSE";
                      }
                    },
                  }}
                  render={({
                    field: { value, onChange },
                  }) => (
                    <StyledToggleButton
                      colors={colors}
                      value={"Voltus"}
                      onClick={() =>
                        onChange(
                          selectedQSE === "Voltus"
                            ? null
                            : "Voltus"
                        )
                      }
                      selected={selectedQSE === "Voltus"}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      {selectedQSE === "Voltus" ? (
                        <CheckIcon fontSize={"small"}/>
                      ) : (
                        ""
                      )}{" "}
                      Voltus
                    </StyledToggleButton>
                  )}
                />

                <Controller
                  name="qse"
                  control={control}
                  defaultValue={null}
                  rules={{
                    required: "QSE is required",
                    validate: (value) => {
                      if (!value) {
                        return "Please select one QSE";
                      }
                    },
                  }}
                  render={({
                    field: { value, onChange },
                  }) => (
                    <StyledToggleButton
                      colors={colors}
                      value={"CPower"}
                      onClick={() =>
                        onChange(
                          selectedQSE === "CPower"
                            ? null
                            : "CPower"
                        )
                      }
                      selected={selectedQSE === "CPower"}
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                    >
                      {selectedQSE === "CPower" ? (
                        <CheckIcon fontSize={"small"} />
                      ) : (
                        ""
                      )}{" "}
                      CPower
                    </StyledToggleButton>
                  )}
                />
              </Box>
            </Box>
            {selectedQSE === "Voltus" && (
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
              >
                <H8TitleMedium>
                  Opt in to voltus dispatch events
                </H8TitleMedium>
                <StyledFormControl colors={colors} component="fieldset">
                  <Controller
                    name="optIn"
                    rules={{
                      required: "Opt-in is required",
                    }}
                    control={control}
                    render={({
                      field,
                      fieldState: { error },
                    }) => (
                      <>
                        <RadioGroup {...field}>
                          <FormControlLabel
                            value="true"
                            control={<StyledRadio colors={colors} />}
                            label="Yes"
                            sx={{ color: colors.text.primary }}
                          />
                          <FormControlLabel
                            value="false"
                            control={<StyledRadio colors={colors} />}
                            label="No"
                            sx={{ color: colors.text.primary }}
                          />
                        </RadioGroup>
                      </>
                    )}
                  />
                </StyledFormControl>
              </Box>
            )}
            {selectedQSE === "CPower" && (
              <>
                <Box
                  display={"grid"}
                  gridTemplateRows={"auto auto"}
                  gap={4}
                >
                  <H8TitleMedium>Username</H8TitleMedium>
                  <Controller
                    name="username"
                    control={control}
                    rules={{
                      required: "Username is required",
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        margin="none"
                        type="username"
                        variant="outlined"
                        label="Username"
                        {...field}
                        error={Boolean(errors.username)}
                        helperText={
                          errors.username
                            ? errors.username.message
                            : ""
                        }
                      />
                    )}
                  />
                </Box>
                <Box
                  display={"grid"}
                  gridTemplateRows={"auto auto"}
                  gap={4}
                >
                  <H8TitleMedium>Password</H8TitleMedium>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: "Password is required",
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        margin="none"
                        type="password"
                        variant="outlined"
                        label="Password"
                        {...field}
                        error={Boolean(errors.password)}
                        helperText={
                          errors.password
                            ? errors.password.message
                            : ""
                        }
                      />
                    )}
                  />
                </Box>
              </>
            )}
            <Box
              display={"grid"}
              gridTemplateRows={"auto auto"}
              gap={4}
              overflowY={"auto"}
            >
              <H8TitleMedium>
                Ramp Up/Down Time
              </H8TitleMedium>
              <Controller
                name="rampUpDownTime"
                control={control}
                rules={{
                  required: "Ramp up/down time is required",
                  max: {
                    value: 86400,
                    message:
                      "Maximum value is 86400 seconds (24 hours)",
                  },
                  min: {
                    value: 120,
                    message:
                      "Minimum value is 120 seconds (2 minutes)",
                  },
                }}
                render={({ field }) => (
                  <StyledTextField colors={colors}
                    margin="none"
                    variant="outlined"
                    type="number"
                    label="In seconds"
                    {...field}
                    error={Boolean(errors.rampUpDownTime)}
                    helperText={
                      errors.rampUpDownTime
                        ? errors.rampUpDownTime.message
                        : "Enter a number between 120 and 86400"
                    }
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ paddingTop: "1rem" }}>
          <PrimaryButton colors={colors} disabled={!isValid} type="submit">
            Save
          </PrimaryButton>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default AssignQSEDialog;
